import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { motion } from "framer-motion";
import { createApiClient, createPublicClient } from "../api/client";
import { FamilyInviteView, FamilyInviteResult } from "../types";
import { track } from "../analytics";
import Button from "../components/ui/Button";

/**
 * The "join the family" page behind a join link (from an SMS or shared in-app).
 * Anonymous-friendly; if the visitor is logged in, accepting also links the
 * member to their account so the family shows up in their dashboard.
 */
const JoinFamilyPage: React.FC = () => {
  const { token = "" } = useParams();
  const { isAuthenticated, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [invite, setInvite] = useState<FamilyInviteView | null>(null);
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<FamilyInviteResult | null>(null);

  const client = useCallback(async () => {
    if (isAuthenticated) {
      try {
        return createApiClient(await getAccessTokenSilently());
      } catch {
        /* fall through to anonymous */
      }
    }
    return createPublicClient();
  }, [isAuthenticated, getAccessTokenSilently]);

  const load = useCallback(async () => {
    try {
      const { data } = await createPublicClient().get<FamilyInviteView>(`/api/family-invite/${token}`);
      setInvite(data);
    } catch {
      setInvalid(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const respond = async (accept: boolean) => {
    setBusy(true);
    try {
      const { data } = await (await client()).post<FamilyInviteResult>(
        `/api/family-invite/${token}/respond`,
        { accept }
      );
      setResult(data);
      if (data.status === "Accepted") track("invite_accepted");
    } catch {
      setInvalid(true);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-primary-500" />
      </div>
    );
  }

  if (invalid || !invite) {
    return (
      <div className="mx-auto mt-16 max-w-md text-center">
        <div className="text-5xl">⌛</div>
        <h1 className="mt-4 font-display text-2xl font-semibold text-ink">This invite link isn't valid</h1>
        <p className="mt-2 text-ink/65">It may have expired or already been used.</p>
      </div>
    );
  }

  return (
    <motion.div
      className="mx-auto mt-10 max-w-md"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="rounded-4xl border border-primary-100 bg-white/80 p-8 text-center shadow-warm backdrop-blur-sm">
        <img src="/logo.svg" alt="Kinoes" className="mx-auto h-14 w-14" />

        {result ? (
          result.status === "Accepted" ? (
            <>
              <h1 className="mt-3 font-display text-3xl font-semibold text-ink">You're in! 🎉</h1>
              <p className="mt-2 text-ink/65">
                You've joined <span className="font-medium text-ink">{result.familyName}</span>. You'll get a
                text whenever they're deciding what to do.
              </p>
              {isAuthenticated && result.familyId ? (
                <Link to={`/families/${result.familyId}/plans`} className="mt-6 inline-block">
                  <Button size="lg">See the family's plans</Button>
                </Link>
              ) : (
                <div className="mt-6 space-y-2">
                  <Button size="lg" fullWidth onClick={() => loginWithRedirect({ authorizationParams: { screen_hint: "signup" } })}>
                    Create an account to help plan
                  </Button>
                  <p className="text-xs text-ink/45">Optional — you'll still get invites by text.</p>
                </div>
              )}
            </>
          ) : (
            <>
              <h1 className="mt-3 font-display text-3xl font-semibold text-ink">No problem 👍</h1>
              <p className="mt-2 text-ink/65">
                You've declined to join {result.familyName}. They won't invite you to plans. Changed your mind?
                Ask them to send the link again.
              </p>
            </>
          )
        ) : invite.alreadyResponded ? (
          <>
            <h1 className="mt-3 font-display text-3xl font-semibold text-ink">Hi {invite.memberName}!</h1>
            <p className="mt-2 text-ink/65">
              You've already responded to your invite to <span className="font-medium text-ink">{invite.familyName}</span>
              {invite.status === "Accepted" ? " — you're in! 🎉" : "."}
            </p>
            {invite.status !== "Accepted" && (
              <Button className="mt-6" size="lg" onClick={() => respond(true)} disabled={busy}>
                Actually, I'm in
              </Button>
            )}
          </>
        ) : (
          <>
            <h1 className="mt-3 font-display text-3xl font-semibold text-ink">Hi {invite.memberName}!</h1>
            <p className="mt-2 text-ink/65">
              You've been invited to join the <span className="font-medium text-ink">{invite.familyName}</span> family
              on Kinoes — so you can help decide what to do together.
            </p>
            <div className="mt-6 space-y-3">
              <Button fullWidth size="lg" onClick={() => respond(true)} disabled={busy} className="text-lg">
                Join {invite.familyName}
              </Button>
              <Button variant="secondary" fullWidth onClick={() => respond(false)} disabled={busy}>
                No thanks
              </Button>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
};

export default JoinFamilyPage;
